import React from "react";
import { shallow } from "zustand/shallow";

import { type FilterConfig, type OptionConfig } from "../../config/types";
import { Translate } from "../../hooks/useTranslation";
import useExplorerStore from "../../store/store";
import { type Filter } from "../../store/types";
import CheckboxList, { type Props as BaseProps } from "./CheckboxList";

type Value = OptionConfig["value"];
type Props = {
    filter: FilterConfig;
    onChange?: (filter: Filter) => void;
} & Pick<BaseProps<Value>, "isOpen" | "values">;

const FilterCheckboxList = ({ filter, isOpen, values, onChange }: Props) => {
    const { filterHasFunds } = useExplorerStore(
        ({ filterHasFunds, search: { term } }) => ({ filterHasFunds, term }),
        shallow,
    );
    const filterObj = {
        prop: filter.prop,
        level: filter.level,
        type: filter.type,
    };
    const handleChange = (values: Value[]) => {
        onChange &&
            onChange({
                ...filterObj,
                value: values.flat(),
            });
    };
    const isDisabled = (value: Value) => {
        return filterHasFunds({
            ...filterObj,
            value: [value].flat(),
        });
    };

    const options = React.useMemo(() => {
        const output = filter.options ?? [];
        if (filter.orderDirective) {
            const { type, direction } = filter.orderDirective;
            switch (type) {
                case "alphabetical":
                    if (direction === "asc") {
                        return output
                            .slice()
                            .sort((a, b) => (a.label < b.label ? -1 : 1));
                    }
                    if (direction === "desc") {
                        return output
                            .slice()
                            .sort((a, b) => (a.label > b.label ? -1 : 1));
                    }
                    break;
                default:
                    break;
            }
        }

        return output;
    }, [filter]);

    return (
        <CheckboxList<Value>
            containerClassName="py-2"
            isOpen={isOpen}
            options={options.map((o) => ({
                ...o,
                disabled: isDisabled(o.value),
            }))}
            title={<Translate>{filter.label}</Translate>}
            tooltip={{ content: filter.tooltip }}
            values={values}
            label={filter.label}
            onChange={handleChange}
            labelClassName="text-sm"
        />
    );
};

export default FilterCheckboxList;
